import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import type { CommentKind } from '../store'
import { useCoachingStore } from '../store'
import { KIND_ACCENT } from './comment-card'

const KINDS: CommentKind[] = ['praise', 'critique', 'suggestion']
const KIND_LABEL: Record<CommentKind, string> = {
  praise: 'Praise',
  critique: 'Critique',
  suggestion: 'Suggestion',
}

/**
 * Floating composer (spec §8.2). Reads `composer` off the review slice —
 * section/target/kind/draft are all store state, so a section header's
 * `+ Comment` click, code-line targeting, etc. all open the same singleton
 * composer, and the typed draft outlives this component. It has to: the
 * composer renders in the gutter overlay, which unmounts it whenever the
 * expanded card's sections leave the DOM — folding the review's week does
 * that without ending the composing session (LG-642).
 *
 * The draft lives and dies with one composing SESSION, not one section.
 * `openComposer` starts a session, so every retarget clears the draft — in
 * every section, not only code: a sentence typed against `Pacing · Total` is
 * not about `Pacing · Topic avg` either, and on code the stale draft would
 * now be persisted against line numbers it was never about. The two
 * in-session mutations keep the draft: `extendComposerLines` widens a line
 * range, and `setComposerKind` swaps the kind pill in place (the .dc
 * reference patches `composer.kind` the same way).
 */
export function Composer(props: { style?: CSSProperties }): ReactNode {
  const { style } = props
  const { composer, closeComposer, post, setComposerKind, setComposerDraft } =
    useCoachingStore().review
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Refocuses once per composing session, which is exactly what `session`
  // identifies: `openComposer` mints a new one for every fresh target
  // (including a retarget inside the same section, e.g. the coach types an
  // overall note on `Code · Overall` and then clicks line 5 — that draft is
  // about something else and must not be posted, or anchored, to L5), while
  // `extendComposerLines` and `setComposerKind` mutate the open session and
  // keep it. Clearing the draft is the session's own business now
  // (`openedComposer` mints it empty), so a remount inside one session — the
  // gutter overlay tearing this component down and putting it back — restores
  // what the coach typed instead of blanking it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `composer` itself is intentionally omitted — the session id is the whole refocus signal, and depending on the object would refocus on every extension and kind switch.
  useEffect(() => {
    if (!composer) return
    textareaRef.current?.focus()
  }, [composer?.session])

  if (!composer) return null

  const text = composer.draft
  const canPost = text.trim().length > 0

  const handlePost = () => {
    if (!canPost) return
    // The anchor has to be posted as `lines` or it is lost: the slice persists
    // `lineStart`/`lineEnd` off this field, so without it a comment on
    // 'Code · L5–L8' reloads with the human label alone and the Code section
    // never highlights it again (LG-517). It comes off `composer.lines`, not
    // off the label — only a composer the coach actually anchored has one.
    post({
      kind: composer.kind,
      section: composer.section,
      target: composer.target,
      targetKey: composer.targetKey,
      body: text.trim(),
      ...(composer.lines === undefined ? {} : { lines: composer.lines }),
    })
    closeComposer()
  }

  return (
    <div
      style={{
        ...style,
        background: 'var(--cd-panel)',
        borderColor: 'var(--cd-border-4)',
      }}
      className="w-[308px] rounded-xl border p-3.5 shadow-[0_10px_34px_rgba(0,0,0,.5)]"
    >
      <div className="mb-2.5 flex items-center gap-1.5">
        <span
          className="rounded-full border border-(--cd-border-3) px-2.5 py-0.5 text-[10.5px] font-semibold text-(--cd-text)"
          style={{
            background: 'color-mix(in oklab, var(--cd-bg) 50%, transparent)',
          }}
        >
          {composer.target}
        </span>
        <button
          type="button"
          onClick={closeComposer}
          aria-label="Close composer"
          className="ml-auto text-[11px] text-(--cd-text-faint) hover:text-(--cd-text)"
        >
          ✕
        </button>
      </div>

      <div className="mb-2.5 flex gap-1.5">
        {KINDS.map((k) => {
          const active = composer.kind === k
          const accent = KIND_ACCENT[k]
          return (
            <button
              key={k}
              type="button"
              onClick={() => setComposerKind(k)}
              className="rounded-full border px-2.5 py-1 text-xs font-semibold"
              style={
                active
                  ? {
                      background: accent.pillBg,
                      color: accent.pillFg,
                      borderColor: accent.pillFg,
                    }
                  : {
                      background: 'transparent',
                      color: 'var(--cd-text-faint)',
                      borderColor: 'var(--cd-border-2)',
                    }
              }
            >
              {KIND_LABEL[k]}
            </button>
          )
        })}
      </div>

      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setComposerDraft(e.target.value)}
        placeholder={`Comment on ${composer.target}…`}
        rows={4}
        className="w-full resize-y rounded-lg border border-(--cd-border-2) bg-(--cd-bg) px-2.5 py-2 text-[12.5px] text-(--cd-text) outline-none"
      />

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          disabled={!canPost}
          onClick={handlePost}
          className="rounded-lg bg-(--cd-green) px-4 py-1.5 text-[12.5px] font-semibold text-(--cd-bg) disabled:cursor-not-allowed"
          style={{ opacity: canPost ? 1 : 0.45 }}
        >
          Post
        </button>
      </div>
    </div>
  )
}
